import RAPIER from '@dimforge/rapier3d-compat';
import * as THREE from 'three';

/**
 * Thin Rapier wrapper. Owns the physics `World` (gravity-free — the arch stones
 * float and are sprung back home, so they never fall) and the helpers that turn
 * Three.js meshes into Rapier bodies/colliders.
 *
 * Rapier ships its WASM inline (the `-compat` build), so `await Physics.create()`
 * is all the setup needed — no Vite plugin, no fetch.
 */
/**
 * Collision groups (Rapier packs membership in the high 16 bits, filter in the low 16).
 * Stones collide with the STRUCTURE only — never each other — so the tightly-packed
 * arch voussoirs don't shove themselves apart at rest. Structure collides with stones.
 */
const STONE = 0x0001;
const STRUCT = 0x0002;
const STONE_GROUPS = (STONE << 16) | STRUCT; // member STONE, collides with STRUCT
const STRUCT_GROUPS = (STRUCT << 16) | STONE; // member STRUCT, collides with STONE

export class Physics {
  readonly world: RAPIER.World;
  /** A single fixed body that owns every static structure collider (pillars, base…). */
  private readonly staticBody: RAPIER.RigidBody;

  private constructor() {
    this.world = new RAPIER.World({ x: 0, y: 0, z: 0 });
    this.world.timestep = 1 / 60;
    this.staticBody = this.world.createRigidBody(RAPIER.RigidBodyDesc.fixed());
  }

  static async create(): Promise<Physics> {
    await RAPIER.init();
    return new Physics();
  }

  /** Advance the simulation. Sub-steps so a long frame stays stable, capped. */
  step(delta: number): void {
    const dt = this.world.timestep;
    let remaining = Math.min(delta, 1 / 15); // never simulate more than ~4 steps/frame
    let guard = 0;
    while (remaining > 1e-4 && guard < 4) {
      this.world.step();
      remaining -= dt;
      guard += 1;
    }
  }

  /**
   * Dynamic body for a moving mesh: a convex hull baked in a frame centred on `home`
   * with the mesh's rest world orientation folded into the points, so the body starts
   * at `home` with identity rotation and the hull lines up with the rendered mesh.
   */
  addDynamicBody(
    mesh: THREE.Mesh,
    home: THREE.Vector3,
    opts: { density: number; linearDamping: number; angularDamping: number; shrink: number },
  ): RAPIER.RigidBody | null {
    // A sphere INSCRIBED in the voussoir (centred at `home`): sphere-vs-trimesh is far
    // more stable than convex-hull-vs-trimesh (no deep-penetration blow-ups), and being
    // smaller than the stone it never touches the structure the stone rests against at
    // rest — yet a hover push of ~0.6u still drives it into a pillar to collide.
    const radius = this.inscribedRadius(mesh, home) * opts.shrink;
    if (!(radius > 0)) return null;
    const desc = RAPIER.ColliderDesc.ball(radius)
      .setDensity(opts.density)
      .setFriction(0.4)
      .setRestitution(0.0)
      .setCollisionGroups(STONE_GROUPS);

    const body = this.world.createRigidBody(
      RAPIER.RigidBodyDesc.dynamic()
        .setTranslation(home.x, home.y, home.z)
        .setLinearDamping(opts.linearDamping)
        .setAngularDamping(opts.angularDamping)
        .lockRotations() // stones SLIDE only — never tumble
        .setCanSleep(false),
    );
    this.world.createCollider(desc, body);
    return body;
  }

  /** Radius of the largest sphere fitting inside the mesh's world-space bounds. */
  private inscribedRadius(mesh: THREE.Mesh, home: THREE.Vector3): number {
    const pos = mesh.geometry.attributes.position;
    mesh.updateWorldMatrix(true, false);
    const m = mesh.matrixWorld;
    const v = new THREE.Vector3();
    let hx = 0;
    let hy = 0;
    let hz = 0;
    for (let i = 0; i < pos.count; i += 1) {
      v.fromBufferAttribute(pos, i).applyMatrix4(m).sub(home);
      hx = Math.max(hx, Math.abs(v.x));
      hy = Math.max(hy, Math.abs(v.y));
      hz = Math.max(hz, Math.abs(v.z));
    }
    return Math.min(hx, hy, hz);
  }

  /** Static trimesh collider (world space) for a structure mesh — pillars, doors, base. */
  addStaticTrimesh(mesh: THREE.Mesh): void {
    const geometry = mesh.geometry;
    const pos = geometry.attributes.position;
    if (!pos) return;

    mesh.updateWorldMatrix(true, false);
    const m = mesh.matrixWorld;
    const v = new THREE.Vector3();
    const verts = new Float32Array(pos.count * 3);
    for (let i = 0; i < pos.count; i += 1) {
      v.fromBufferAttribute(pos, i).applyMatrix4(m);
      verts[i * 3] = v.x;
      verts[i * 3 + 1] = v.y;
      verts[i * 3 + 2] = v.z;
    }

    let indices: Uint32Array;
    if (geometry.index) {
      indices = new Uint32Array(geometry.index.array);
    } else {
      indices = new Uint32Array(pos.count);
      for (let i = 0; i < pos.count; i += 1) indices[i] = i;
    }

    const desc = RAPIER.ColliderDesc.trimesh(verts, indices);
    if (desc) {
      desc.setCollisionGroups(STRUCT_GROUPS);
      this.world.createCollider(desc, this.staticBody);
    }
  }

  /** Big thin static floor at `y` so nothing can be driven below the water. */
  addFloor(y: number, halfExtent = 200): void {
    const desc = RAPIER.ColliderDesc.cuboid(halfExtent, 0.5, halfExtent)
      .setTranslation(0, y - 0.5, 0)
      .setCollisionGroups(STRUCT_GROUPS);
    this.world.createCollider(desc, this.staticBody);
  }

  dispose(): void {
    this.world.free();
  }
}
